import React, { useState, useEffect } from 'react'
import {
    updatePassword,
    reauthenticateWithCredential,
    EmailAuthProvider,
} from 'firebase/auth'
import { doc, getDoc } from 'firebase/firestore'
import { auth, firestore } from '../../config/firebase'
import { useAuth } from '../../contexts/AuthContext'
import useGarage from '../../wrappers/use-garage'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Paper from '@mui/material/Paper'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import Grid from '@mui/material/Grid'
import Alert from '@mui/material/Alert'
import { Save } from 'lucide-react'
import { MuiTelInput } from 'mui-tel-input'
import { useTranslation } from 'react-i18next'
import { profilePageStyles } from './Profile.styles'
import { useTelInputSettings } from '../../utils/phone'

interface Msg {
    type: 'success' | 'error'
    text: string
}

const Profile: React.FC = () => {
    const { t } = useTranslation()
    const { user, updateUserProfile } = useAuth()
    const { get: getGarage, edit: editGarage } = useGarage()
    const telInputSettings = useTelInputSettings()

    const [personalData, setPersonalData] = useState({ firstName: '', lastName: '', phone: '' })
    const [garageData, setGarageData] = useState({ street: '', houseNumber: '', city: '', zip: '' })
    const [passwordData, setPasswordData] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' })

    const [isEditingPersonal, setIsEditingPersonal] = useState(false)
    const [isEditingGarage, setIsEditingGarage] = useState(false)

    const [savingPersonal, setSavingPersonal] = useState(false)
    const [savingGarage, setSavingGarage] = useState(false)
    const [savingPassword, setSavingPassword] = useState(false)

    const [personalMsg, setPersonalMsg] = useState<Msg | null>(null)
    const [garageMsg, setGarageMsg] = useState<Msg | null>(null)
    const [passwordMsg, setPasswordMsg] = useState<Msg | null>(null)

    const isEmailUser = auth.currentUser?.providerData.some(p => p.providerId === 'password') ?? false

    useEffect(() => {
        if (!user) return
        const load = async () => {
            const snap = await getDoc(doc(firestore, 'user', user.uid))
            if (!snap.exists()) return
            const d = snap.data()
            setPersonalData({
                firstName: d.firstName || '',
                lastName: d.lastName || '',
                phone: d.phone || '',
            })
            if (user.garageUid) {
                const garage = await getGarage(user.garageUid)
                if (garage) {
                    const address = garage.address
                    setGarageData({
                        street: address.street || '',
                        houseNumber: address.houseNumber || '',
                        city: address.city || '',
                        zip: address.zip || '',
                    })
                }
            }
        }
        load()
    }, [user, getGarage])

    const handleSavePersonal = async (e: React.FormEvent) => {
        e.preventDefault()
        setPersonalMsg(null)
        setSavingPersonal(true)
        try {
            await updateUserProfile({
                firstName: personalData.firstName,
                lastName: personalData.lastName,
                phone: personalData.phone,
            })
            setIsEditingPersonal(false)
            setPersonalMsg({ type: 'success', text: t('profile.personal.savedSuccess') })
        } catch {
            setPersonalMsg({ type: 'error', text: t('profile.personal.saveFailed') })
        } finally {
            setSavingPersonal(false)
        }
    }

    const handleSaveGarage = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!user?.garageUid) return
        setGarageMsg(null)
        setSavingGarage(true)
        try {
            await editGarage(user.garageUid, {
                address: {
                    street: garageData.street,
                    houseNumber: garageData.houseNumber,
                    city: garageData.city,
                    zip: garageData.zip,
                },
            })
            setIsEditingGarage(false)
            setGarageMsg({ type: 'success', text: t('profile.garage.savedSuccess') })
        } catch {
            setGarageMsg({ type: 'error', text: t('profile.garage.saveFailed') })
        } finally {
            setSavingGarage(false)
        }
    }

    const handleChangePassword = async (e: React.FormEvent) => {
        e.preventDefault()
        setPasswordMsg(null)
        if (passwordData.newPassword !== passwordData.confirmPassword) {
            setPasswordMsg({ type: 'error', text: t('profile.password.mismatch') })
            return
        }
        if (passwordData.newPassword.length < 6) {
            setPasswordMsg({ type: 'error', text: t('profile.password.tooShort') })
            return
        }
        setSavingPassword(true)
        try {
            const currentUser = auth.currentUser
            if (!currentUser?.email) throw new Error('No user')
            const credential = EmailAuthProvider.credential(currentUser.email, passwordData.currentPassword)
            await reauthenticateWithCredential(currentUser, credential)
            await updatePassword(currentUser, passwordData.newPassword)
            setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' })
            setPasswordMsg({ type: 'success', text: t('profile.password.changedSuccess') })
        } catch (err) {
            const code = (err as { code?: string }).code
            if (code === 'auth/wrong-password' || code === 'auth/invalid-credential') {
                setPasswordMsg({ type: 'error', text: t('profile.password.wrongCurrent') })
            } else {
                setPasswordMsg({ type: 'error', text: t('profile.password.changeFailed') })
            }
        } finally {
            setSavingPassword(false)
        }
    }

    return (
        <Box sx={profilePageStyles.root}>
            <Box>
                <Typography variant="h4" fontWeight={700}>{t('profile.title')}</Typography>
                <Typography color="text.secondary" mt={0.5}>{t('profile.subtitle')}</Typography>
            </Box>

            {/* ─── Osobní údaje ─────────────────────────────────────── */}
            <Paper elevation={0} sx={profilePageStyles.card}>
                <Box sx={profilePageStyles.cardHeader}>
                    <Typography variant="h6" fontWeight={600}>{t('profile.personal.title')}</Typography>
                    <Button size="small" onClick={() => { setIsEditingPersonal(!isEditingPersonal); setPersonalMsg(null) }}>
                        {isEditingPersonal ? t('common.cancel') : t('common.edit')}
                    </Button>
                </Box>
                <Box sx={profilePageStyles.cardContent}>
                    {personalMsg && (
                        <Alert severity={personalMsg.type} sx={profilePageStyles.messageAlert}>{personalMsg.text}</Alert>
                    )}
                    <Box component="form" onSubmit={handleSavePersonal}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label={t('common.firstName')}
                                    fullWidth
                                    disabled={!isEditingPersonal}
                                    value={personalData.firstName}
                                    onChange={(e) => setPersonalData({ ...personalData, firstName: e.target.value })}
                                    autoComplete="given-name"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label={t('common.lastName')}
                                    fullWidth
                                    disabled={!isEditingPersonal}
                                    value={personalData.lastName}
                                    onChange={(e) => setPersonalData({ ...personalData, lastName: e.target.value })}
                                    autoComplete="family-name"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label={t('common.email')}
                                    type="email"
                                    fullWidth
                                    disabled
                                    value={user?.email || ''}
                                    helperText={t('profile.personal.emailHelp')}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <MuiTelInput
                                    label={t('common.phone')}
                                    fullWidth
                                    disabled={!isEditingPersonal}
                                    value={personalData.phone}
                                    onChange={(value) => setPersonalData({ ...personalData, phone: value })}
                                    {...telInputSettings}
                                    autoComplete="tel"
                                />
                            </Grid>
                        </Grid>
                        {isEditingPersonal && (
                            <Box sx={profilePageStyles.saveActions}>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    startIcon={<Save size={20} />}
                                    disabled={savingPersonal}
                                >
                                    {savingPersonal ? t('common.saving') : t('common.saveChanges')}
                                </Button>
                            </Box>
                        )}
                    </Box>
                </Box>
            </Paper>

            {/* ─── Údaje garáže ─────────────────────────────────────── */}
            <Paper elevation={0} sx={profilePageStyles.card}>
                <Box sx={profilePageStyles.cardHeader}>
                    <Typography variant="h6" fontWeight={600}>{t('profile.garage.title')}</Typography>
                    <Button size="small" onClick={() => { setIsEditingGarage(!isEditingGarage); setGarageMsg(null) }}>
                        {isEditingGarage ? t('common.cancel') : t('common.edit')}
                    </Button>
                </Box>
                <Box sx={profilePageStyles.cardContent}>
                    {garageMsg && (
                        <Alert severity={garageMsg.type} sx={profilePageStyles.messageAlert}>{garageMsg.text}</Alert>
                    )}
                    <Box component="form" onSubmit={handleSaveGarage}>
                        <Grid container spacing={2}>
                            <Grid item xs={8}>
                                <TextField
                                    label={t('common.street')}
                                    fullWidth
                                    disabled={!isEditingGarage}
                                    value={garageData.street}
                                    onChange={(e) => setGarageData({ ...garageData, street: e.target.value })}
                                    autoComplete="address-line1"
                                />
                            </Grid>
                            <Grid item xs={4}>
                                <TextField
                                    label={t('common.houseNumber')}
                                    fullWidth
                                    disabled={!isEditingGarage}
                                    value={garageData.houseNumber}
                                    onChange={(e) => setGarageData({ ...garageData, houseNumber: e.target.value })}
                                />
                            </Grid>
                            <Grid item xs={8}>
                                <TextField
                                    label={t('common.city')}
                                    fullWidth
                                    disabled={!isEditingGarage}
                                    value={garageData.city}
                                    onChange={(e) => setGarageData({ ...garageData, city: e.target.value })}
                                    autoComplete="address-level2"
                                />
                            </Grid>
                            <Grid item xs={4}>
                                <TextField
                                    label={t('common.zip')}
                                    fullWidth
                                    disabled={!isEditingGarage}
                                    value={garageData.zip}
                                    onChange={(e) => setGarageData({ ...garageData, zip: e.target.value })}
                                    autoComplete="postal-code"
                                />
                            </Grid>
                        </Grid>
                        {isEditingGarage && (
                            <Box sx={profilePageStyles.saveActions}>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    startIcon={<Save size={20} />}
                                    disabled={savingGarage}
                                >
                                    {savingGarage ? t('common.saving') : t('common.saveChanges')}
                                </Button>
                            </Box>
                        )}
                    </Box>
                </Box>
            </Paper>

            {/* ─── Změna hesla (jen pro e-mailové uživatele) ────────── */}
            {isEmailUser && (
                <Paper elevation={0} sx={profilePageStyles.card}>
                    <Box sx={profilePageStyles.passwordHeader}>
                        <Typography variant="h6" fontWeight={600}>{t('profile.password.title')}</Typography>
                        <Typography variant="body2" color="text.secondary">{t('profile.password.subtitle')}</Typography>
                    </Box>
                    <Box sx={profilePageStyles.cardContent}>
                        {passwordMsg && (
                            <Alert severity={passwordMsg.type} sx={profilePageStyles.messageAlert}>{passwordMsg.text}</Alert>
                        )}
                        <Box component="form" onSubmit={handleChangePassword}>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={8}>
                                    <TextField
                                        label={t('profile.password.currentPassword')}
                                        type="password"
                                        fullWidth
                                        value={passwordData.currentPassword}
                                        onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                                        autoComplete="current-password"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={8}>
                                    <TextField
                                        label={t('profile.password.newPassword')}
                                        type="password"
                                        fullWidth
                                        value={passwordData.newPassword}
                                        onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                                        autoComplete="new-password"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={8}>
                                    <TextField
                                        label={t('profile.password.confirmNewPassword')}
                                        type="password"
                                        fullWidth
                                        value={passwordData.confirmPassword}
                                        onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                                        autoComplete="new-password"
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <Button
                                        type="submit"
                                        variant="outlined"
                                        color="inherit"
                                        disabled={savingPassword}
                                    >
                                        {savingPassword ? t('profile.password.changing') : t('profile.password.changeButton')}
                                    </Button>
                                </Grid>
                            </Grid>
                        </Box>
                    </Box>
                </Paper>
            )}
        </Box>
    )
}

export default Profile
